import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Paper,
    Stack,
    Typography,
    Avatar,
    Box,
    Divider
} from "@mui/material";
import ReplyIcon from "@mui/icons-material/Reply";
import { MessageComposer } from "features/Messages/MessageComposer";

// ✅ Utility: group messages by thread and sort by latest timestamp
function groupByMessageId(messages) {
    const groups = messages.reduce((map, msg) => {
        const arr = map.get(msg.messageId) || [];
        arr.push(msg);
        map.set(msg.messageId, arr);
        return map;
    }, new Map());

    return Array.from(groups.entries())
        .map(([messageId, msgs]) => ({
            messageId,
            messages: msgs.sort(
                (a, b) => (a.clientTimestamp ?? 0) - (b.clientTimestamp ?? 0)
            )
        }))
        .sort((a, b) => {
            const aLast = a.messages.at(-1)?.clientTimestamp ?? 0;
            const bLast = b.messages.at(-1)?.clientTimestamp ?? 0;
            return bLast - aLast;
        });
}

// ✅ Utility: format timestamp nicely
function formatTime(ts) {
    if (!ts) return "";
    return new Date(ts).toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit"
    });
}

export function ReadMessages({ open, onClose }) {
    const myMessages = useSelector((state) => state.message.myMessages);
    const currentUserId = useSelector((state) => state.message.currentUserId);

    const [replyTo, setReplyTo] = useState(null);
    const [composerOpen, setComposerOpen] = useState(false);

    const threads = useMemo(() => groupByMessageId(myMessages), [myMessages]);

    const handleReply = (thread) => {
        const otherMessage = [...thread.messages]
            .reverse()
            .find((m) => m.fromUserId !== currentUserId);
        const targetUserId =
            otherMessage?.fromUserId ?? thread.messages[0]?.fromUserId;

        setReplyTo({ userId: targetUserId, messageId: thread.messageId });
        setComposerOpen(true);
    };

    const handleNewMessage = () => {
        setReplyTo(null);
        setComposerOpen(true);
    };

    // ✅ Render a single message bubble
    const renderBubble = (message, index, thread) => {
        const isMine = message.fromUserId === currentUserId;
        const prev = thread.messages[index - 1];
        const showSender = !prev || prev.fromUserId !== message.fromUserId;

        return (
            <Box
                key={`${thread.messageId}-${index}`}
                sx={{
                    display: "flex",
                    justifyContent: isMine ? "flex-end" : "flex-start",
                    alignItems: "flex-end",
                    gap: 1,
                    mt: showSender ? 1.5 : 0.5
                }}
            >
                {!isMine && (
                    <Avatar
                        sx={{
                            width: 28,
                            height: 28,
                            fontSize: 13,
                            visibility: showSender ? "visible" : "hidden"
                        }}
                    >
                        {message.fromUserId}
                    </Avatar>
                )}

                <Box sx={{ maxWidth: "70%" }}>
                    {showSender && (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                                textAlign: isMine ? "right" : "left",
                                mb: 0.3,
                                px: 0.5
                            }}
                        >
                            {isMine ? "You" : message.fromUserId}
                        </Typography>
                    )}

                    <Box
                        sx={{
                            bgcolor: isMine ? "primary.main" : "grey.100",
                            color: isMine
                                ? "primary.contrastText"
                                : "text.primary",
                            px: 1.5,
                            py: 1,
                            borderRadius: 2,
                            borderBottomRightRadius: isMine ? 4 : 16,
                            borderBottomLeftRadius: isMine ? 16 : 4,
                            boxShadow: 1
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                whiteSpace: "pre-line",
                                wordBreak: "break-word"
                            }}
                        >
                            {message.message}
                        </Typography>
                    </Box>

                    {message.clientTimestamp && (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                                textAlign: isMine ? "right" : "left",
                                mt: 0.3,
                                px: 0.5
                            }}
                        >
                            {formatTime(message.clientTimestamp)}
                        </Typography>
                    )}
                </Box>
            </Box>
        );
    };

    // ✅ Render a thread with divider and reply button
    const renderThread = (thread) => (
        <Paper
            key={thread.messageId}
            variant="outlined"
            sx={{
                p: 2,
                mb: 2,
                borderRadius: 3,
                bgcolor: "background.paper",
                transition: "background 0.2s ease-in-out",
                "&:hover": { bgcolor: "grey.100" }
            }}
        >
            <Stack spacing={0}>
                {thread.messages.map((message, index) =>
                    renderBubble(message, index, thread)
                )}
            </Stack>

            <Divider sx={{ my: 1.5 }} />

            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                    size="small"
                    startIcon={<ReplyIcon fontSize="small" />}
                    onClick={() => handleReply(thread)}
                >
                    Reply
                </Button>
            </Box>
        </Paper>
    );

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogTitle>Messages</DialogTitle>
                <DialogContent dividers sx={{ bgcolor: "grey.50" }}>
                    {threads.length === 0 ? (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ textAlign: "center", py: 4 }}
                        >
                            No messages found.
                        </Typography>
                    ) : (
                        threads.map(renderThread)
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Close</Button>
                    <Button variant="contained" onClick={handleNewMessage}>
                        New Message
                    </Button>
                </DialogActions>
            </Dialog>

            <MessageComposer
                open={composerOpen}
                onClose={() => setComposerOpen(false)}
                replyTo={replyTo}
            />
        </>
    );
}
